import { Head } from "@inertiajs/react"
import Navbar from "@/components/chalky/Navbar"
import Footer from "@/components/chalky/Footer"

interface LegalLayoutProps {
  title: string
  titleHighlight?: string
  subtitle: string
  lastUpdated: string
  lastUpdatedLabel?: string
  lang?: string
  children: React.ReactNode
}

export function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <div className="mb-12">
      <h2 className="text-xl md:text-2xl font-bold text-white mb-6 pb-3 border-b border-white/10">
        <span className="text-violet-400/70">{number}.</span> {title}
      </h2>
      {children}
    </div>
  )
}

export function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white/[0.03] border border-white/[0.07] rounded-xl p-6 ${className ?? ""}`}>
      {children}
    </div>
  )
}

export function Grid({ cols = 2, children }: { cols?: 2 | 3; children: React.ReactNode }) {
  const colClass = cols === 3 ? "md:grid-cols-3" : "md:grid-cols-2"
  return <div className={`grid grid-cols-1 ${colClass} gap-6`}>{children}</div>
}

export function InfoBox({
  icon,
  title,
  children,
}: {
  icon?: string
  title?: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white/[0.03] border-l-2 border-white/20 p-6 rounded-r-xl mt-6">
      {title && (
        <p className="font-semibold text-slate-200 mb-2">
          {icon && <i className={`${icon} text-slate-400 mr-2`} />}
          {title}
        </p>
      )}
      <div className="text-slate-400 text-sm leading-relaxed">{children}</div>
    </div>
  )
}

export function ContactCTA({ title, subtitle, secondaryHref, secondaryLabel }: {
  title: string
  subtitle: string
  secondaryHref?: string
  secondaryLabel?: string
}) {
  return (
    <div className="mt-16 text-center">
      <div className="bg-white/[0.03] border border-white/[0.07] p-10 rounded-2xl">
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-slate-400 mb-6">{subtitle}</p>
        <div className="flex gap-4 justify-center flex-wrap">
          <a
            href="mailto:hello@chalky.fr"
            className="px-8 py-3 bg-violet-600 text-white font-semibold rounded-full hover:bg-violet-500 transition-colors"
          >
            Nous contacter
          </a>
          {secondaryHref && (
            <a
              href={secondaryHref}
              className="px-8 py-3 text-slate-300 font-semibold rounded-full border border-white/10 hover:bg-white/5 transition-colors"
            >
              {secondaryLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default function LegalLayout({
  title,
  titleHighlight,
  subtitle,
  lastUpdated,
  lastUpdatedLabel = "Dernière mise à jour :",
  lang,
  children,
}: LegalLayoutProps) {
  const fullTitle = titleHighlight ? `${title} ${titleHighlight}` : title

  return (
    <>
      <Head>
        <title>{`${fullTitle} — Chalky`}</title>
        <meta name="description" content={subtitle} />
      </Head>

      <div className="bg-[#0a0a0f] min-h-screen text-slate-300">
        <Navbar />

        {/* Hero */}
        <section lang={lang} className="pt-32 pb-16 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {title}
              {titleHighlight && (
                <> <span className="text-slate-400">{titleHighlight}</span></>
              )}
            </h1>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">{subtitle}</p>
            <p className="text-sm text-slate-600 mt-4">{lastUpdatedLabel} {lastUpdated}</p>
          </div>
        </section>

        {/* Content */}
        <main lang={lang} className="max-w-4xl mx-auto px-6 pb-24">
          {children}
        </main>

        <Footer />
      </div>
    </>
  )
}
